import { create } from 'zustand';

/**
 * Modo de demonstração: desbloqueia todas as lições para poder percorrer a
 * aplicação inteira sem cumprir os pré-requisitos.
 *
 * **Não existe em produção.** As duas condições abaixo são substituídas por
 * constantes na compilação, o que faz o empacotador remover tudo isto do
 * pacote de produção — não é uma verificação que se possa contornar, é código
 * que lá não chega a existir. Uma porta destas na aplicação real seria uma
 * forma de qualquer pessoa saltar a sequência pedagógica.
 *
 * São duas condições e não uma, e a segunda é a que faz falta na prática:
 *
 * - `import.meta.env.DEV` cobre o `npm run dev` nesta máquina.
 * - `VITE_DEMO=1` cobre o **ambiente de testes** (dev-code.yelela.net), que é
 *   compilado como produção e por isso não tem a primeira. É lá que o
 *   conteúdo se revê num telemóvel a sério, que é um dos dois pontos da
 *   Definição de Concluído — e sem isto seria preciso fazer 68 lições para
 *   chegar à 69.ª. A compilação de produção não define a variável, e a
 *   condição fica falsa antes de o código ser escrito para o disco.
 *
 * Liga-se e desliga-se pelo botão no catálogo, e continua a aceitar `?demo=1`
 * e `?demo=0` no endereço. É sempre anunciado no ecrã: um modo especial
 * silencioso acaba sempre com alguém a reportar como defeito o que é a sua
 * própria configuração.
 *
 * Verificado no pacote compilado: nem a chave de armazenamento nem as classes
 * do interruptor lá aparecem. O que sobrevive são as **frases** do dicionário
 * de `pt-PT.ts`, que é um objecto só e não é podado chave a chave — cento e
 * poucos bytes de texto que nada lê. Pôr uma pessoa a ver o botão exigia o
 * código que não está lá.
 */

const CHAVE = 'modo-demonstracao';

/** Este pacote traz sequer o modo de demonstração? Falso em produção. */
export const demonstracaoDisponivel: boolean =
  import.meta.env.DEV || import.meta.env.VITE_DEMO === '1';

function lerDoArmazenamento(): boolean {
  if (!demonstracaoDisponivel) return false;
  try {
    const pedido = new URLSearchParams(window.location.search).get('demo');
    if (pedido === '1') localStorage.setItem(CHAVE, '1');
    if (pedido === '0') localStorage.removeItem(CHAVE);
    return localStorage.getItem(CHAVE) === '1';
  } catch {
    return false;
  }
}

interface EstadoDaDemonstracao {
  activo: boolean;
  alternar(): void;
}

/**
 * O estado vive num `store` e não numa leitura directa do `localStorage`
 * porque o botão tem de mudar o ecrã **sem recarregar**: o catálogo, o
 * caminho do módulo e a rota da lição decidem o bloqueio a partir daqui, e
 * todos têm de reagir ao mesmo tempo. Recarregar a página funcionava e era
 * pior — quem está a rever conteúdo perde o sítio onde ia.
 */
export const useDemonstracao = create<EstadoDaDemonstracao>()((set, get) => ({
  activo: lerDoArmazenamento(),
  alternar: () => {
    if (!demonstracaoDisponivel) return;
    const proximo = !get().activo;
    try {
      if (proximo) localStorage.setItem(CHAVE, '1');
      else localStorage.removeItem(CHAVE);
    } catch {
      // Sem armazenamento (navegação privada), o modo vale para esta sessão.
    }
    set({ activo: proximo });
  },
}));
